import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ContactStatusBadge } from "./ContactStatusBadge";
import { OnboardingStatusBadge, type OnboardingStatus } from "./OnboardingStatusBadge";

interface Contact {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  job_title?: string | null;
  relationship_status?: string | null;
  customer_since?: string | null;
  created_at?: string | null;
  onboarding_status?: OnboardingStatus;
  last_seen_at?: string | null;
  organizations?: { name: string } | null;
  partner_ownership_status?: string | null;
}

interface ContactListViewProps {
  contacts: Contact[];
  onEdit: (contact: Contact) => void;
}

export const ContactListView = ({ contacts, onEdit }: ContactListViewProps) => {
  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="font-semibold">Name</TableHead>
              <TableHead className="font-semibold">Organisation</TableHead>
              <TableHead className="font-semibold">Position</TableHead>
              <TableHead className="font-semibold">Email</TableHead>
              <TableHead className="font-semibold">Status</TableHead>
              <TableHead className="font-semibold">Onboarding</TableHead>
              <TableHead className="font-semibold">Last Seen</TableHead>
              <TableHead className="font-semibold">Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {contacts.map((contact) => (
              <TableRow
                key={contact.id}
                onClick={() => onEdit(contact)}
                className="cursor-pointer hover:bg-muted/50 transition-colors"
              >
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    {contact.first_name} {contact.last_name}
                    {contact.partner_ownership_status === 'pending' && (
                      <Badge className="text-[10px] px-1.5 py-0 bg-amber-100 text-amber-800 border border-amber-300">
                        Pending
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {contact.organizations?.name || "-"}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {contact.job_title || "-"}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {contact.email || "-"}
                </TableCell>
                <TableCell>
                  <ContactStatusBadge
                    relationshipStatus={
                      contact.relationship_status === "Customer"
                        ? "Customer"
                        : "Prospect"
                    }
                    customerSince={contact.customer_since || undefined}
                    size="sm"
                  />
                </TableCell>
                <TableCell>
                  <OnboardingStatusBadge status={contact.onboarding_status} />
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {contact.onboarding_status === 'onboarded' && contact.last_seen_at
                    ? format(new Date(contact.last_seen_at), "dd MMM yyyy, HH:mm")
                    : "-"}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {contact.created_at
                    ? format(new Date(contact.created_at), "dd MMM yyyy")
                    : "-"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
